/**
 * The user management screen's own API calls.
 *
 * Kept local to this feature rather than added to `@/lib/api`, for the same
 * reason the audit viewer's are (`features/settings/audit/api.ts`): a feature
 * owns the endpoints only it speaks to, so a session's PR touches no file
 * another session also owns.
 *
 * `UsersBackend` is the seam the demo client plugs into: `usersBackendFor`
 * hands back the mock's in-memory store when the app is running on the demo
 * client, so the screen is developable and testable with no API at all.
 */

import type { ApiClient } from '@/api/client'
import { ApiError, type ApiErrorBody } from '@/lib/api'

import type { Invite, InviteInput, Member } from './types'

const BASE = '/api/v1/auth'

export interface UsersBackend {
  members(): Promise<Member[]>
  /** Ends that member's sessions server-side; the new role is returned. */
  changeRole(userId: string, role: string): Promise<Member>
  removeMember(userId: string): Promise<void>
  /** Pending invitations only — accepted and revoked ones are history. */
  invites(): Promise<Invite[]>
  invite(input: InviteInput): Promise<Invite>
  /** Issues a fresh token and sends it again, invalidating the previous link. */
  resendInvite(inviteId: string): Promise<Invite>
  revokeInvite(inviteId: string): Promise<Invite>
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json', ...(init?.headers ?? {}) } : init?.headers,
  })
  if (!response.ok) {
    let body: ApiErrorBody | undefined
    try {
      body = (await response.json()) as ApiErrorBody
    } catch {
      body = undefined
    }
    throw new ApiError(
      response.status,
      body?.error.code ?? 'temporary_failure',
      body?.error.message ?? response.statusText,
      body?.correlation_id,
    )
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const usersApi: UsersBackend = {
  members: () => request<Member[]>('/members'),
  changeRole: (userId, role) =>
    request<Member>(`/members/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  removeMember: (userId) =>
    request<void>(`/members/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
  invites: () => request<Invite[]>('/invites'),
  invite: (input) => request<Invite>('/invites', { method: 'POST', body: JSON.stringify(input) }),
  resendInvite: (inviteId) =>
    request<Invite>(`/invites/${encodeURIComponent(inviteId)}/resend`, { method: 'POST' }),
  revokeInvite: (inviteId) =>
    request<Invite>(`/invites/${encodeURIComponent(inviteId)}`, { method: 'DELETE' }),
}

interface WithUsers {
  readonly users: UsersBackend
}

function hasUsers(client: ApiClient): client is ApiClient & WithUsers {
  return typeof (client as Partial<WithUsers>).users === 'object'
}

/** The demo client's in-memory store when there is one, the real API otherwise. */
export function usersBackendFor(client: ApiClient): UsersBackend {
  return hasUsers(client) ? client.users : usersApi
}
